import React, { useCallback, useState } from "react";
import Layout from "@/components/Layout/Layout";
import { withAdmin } from "@/services/withAdmin";
import { Button, Skeleton } from "@mui/material";
import { IAdminFireBase } from "@/store/models/admin.models";
import audioIcon from "@/assets/icons/music.svg";
import videoIcon from "@/assets/icons/video.svg";
import styles from "./HomeContent.module.scss";
import { IMedia } from "@/store/models/video.models";
import { VideoCard } from "@/components/VideoCard/VideoCard";
import { AudioCard } from "@/components/AudioCard/AudioCard";
import { AddFeaturedItem } from "../AddFeaturedItem/AddFeaturedItem";
import EditVideoModal from "@/pages/ContentPage/components/EditVideoModal/EditVideoModal";
import DeleteModal from "@/components/DeleteModal/DeleteModal";
import EditMusicModal from "@/pages/MusicPage/components/EditMusicModal/EditMusicModal";
import {
  useGetRecommendedMediaQuery,
  useUpdateMediaMutation,
} from "@/store/rtk-api/media.api";

const HomeContentCmp: React.FC<{ admin: IAdminFireBase }> = ({ admin }) => {
  const [selectedVideo, setSelectedVideo] = useState<IMedia | null>(null);
  const [selectedAudio, setSelectedAudio] = useState<IMedia | null>(null);
  const [isOpenAddVideo, setIsOpenAddVideo] = useState(false);
  const [isOpenAddMusic, setIsOpenAddMusic] = useState(false);
  const [isOpenModalDeleteVideo, setIsOpenModalDeleteVideo] = useState(false);
  const [isOpenModalDeleteMusic, setIsOpenModalDeleteMusic] = useState(false);
  const [isOpenEditModal, setIsOpenEditModal] = useState(false);
  const [isOpenEditModalAudio, setIsOpenEditModalAudio] = useState(false);

  const { data: musicData } = useGetRecommendedMediaQuery({
    mediaType: "audio",
  });

  const {
    data: videoData,
    isSuccess,
    refetch,
  } = useGetRecommendedMediaQuery({
    mediaType: "video",
  });

  const handleUpdateIsRecommended = async () => {
    if (selectedVideo) {
      try {
        const updatedSections = selectedVideo.sections.includes(
          "Recommendation"
        )
          ? selectedVideo.sections.filter(
              (section) => section !== "Recommendation"
            )
          : [...selectedVideo.sections, "Recommendation"];
        await updateMedia({
          uid: selectedVideo.uid,
          sections: updatedSections,
        }).unwrap();
      } catch (error) {
        console.error("Failed to update the video:", error);
      }
    }
    setIsOpenModalDeleteVideo(false);
  };
  const [updateMedia] = useUpdateMediaMutation();

  const handleUpdateIsRecommendedSong = async () => {
    if (selectedAudio) {
      try {
        const updatedSections = selectedAudio.sections.includes(
          "Recommendation"
        )
          ? selectedAudio.sections.filter(
              (section) => section !== "Recommendation"
            )
          : [...selectedAudio.sections, "Recommendation"];
        await updateMedia({
          uid: selectedAudio.uid,
          sections: updatedSections,
        }).unwrap();
      } catch (error) {
        console.error("Failed to update the music:", error);
      }
    }
    setIsOpenModalDeleteMusic(false);
  };

  const handleToggleEditModal = (video: IMedia) => {
    setSelectedVideo(video);
    setIsOpenEditModal(true);
  };

  const handleToggleEditModalAudio = (audio: IMedia) => {
    setSelectedAudio(audio);
    setIsOpenEditModalAudio(true);
  };

  const handleToggleDeleteVideoModal = (video: IMedia) => {
    setSelectedVideo(video);
    setIsOpenModalDeleteVideo(true);
  };

  const handleToggleDeleteAudioModal = (audio: IMedia) => {
    setSelectedAudio(audio);
    setIsOpenModalDeleteMusic(true);
  };

  const handleToggleVideoModal = () => {
    setIsOpenAddVideo((prev) => !prev);
  };

  const handleToggleMusicModal = () => {
    setIsOpenAddMusic((prev) => !prev);
  };

  const handleEditVideo = useCallback(
    async (formData: FormData) => {
      await updateMedia(formData);
      setIsOpenEditModal(false);
      refetch();
    },
    [updateMedia, refetch]
  );

  const handleEditMusic = useCallback(async () => {
    if (selectedAudio) {
      await updateMedia({
        uid: selectedAudio.uid,
        title: selectedAudio.title,
        description: selectedAudio.description,
        url: selectedAudio.url,
      });
      setIsOpenEditModalAudio(false);
      refetch();
    }
  }, [updateMedia, selectedAudio, refetch]);

  return (
    <Layout admin={admin}>
      <div className={styles.homeContent}>
        <div className={styles.videos}>
          <div className={styles.topSection}>
            <div className={styles.topSectionTitle}>
              <p>Videos</p>
              <img src={videoIcon} alt="video" />
            </div>
            <Button
              sx={{
                textTransform: "capitalize",
                height: "44px",
                width: "174px",
                backgroundColor: "#008000",
                borderRadius: "12px",
                "&:hover": {
                  backgroundColor: "#008000",
                },
              }}
              variant="contained"
              onClick={handleToggleVideoModal}
            >
              Add item
            </Button>
          </div>
          
          <div className={styles.videosContainer}>
            {isSuccess &&
              videoData.length > 0 &&
              videoData
                .filter((video) => video.sections.includes("Recommendation"))
                .filter(
                  (video, index, self) =>
                    index === self.findIndex((v) => v.uid === video.uid)
                )
                .map((video) => (
                  <VideoCard
                    key={video.uid}
                    video={video}
                    isEdit={true}
                    handleToggleEditModal={handleToggleEditModal}
                    handleToggleDeleteVideoModal={handleToggleDeleteVideoModal}
                  />
                ))}
            {videoData?.filter((video) =>
              video.sections.includes("Recommendation")
            ).length === 0 && <p style={{ marginLeft: "20px" }}>No data yet</p>}
            {isOpenEditModal && (
              <EditVideoModal
                isOpenEditModal={isOpenEditModal}
                setIsOpenEditModal={setIsOpenEditModal}
                selectedVideo={selectedVideo}
                setSelectedVideo={setSelectedVideo}
                handleEditVideo={handleEditVideo}
              />
            )}
            {isOpenModalDeleteVideo && (
              <DeleteModal
                isOpen={isOpenModalDeleteVideo}
                setIsOpen={setIsOpenModalDeleteVideo}
                name={selectedVideo ? selectedVideo.title : ""}
                supportingText="Remove video? The video will be removed from the Featured tab. Don't worry, it will not be deleted from the app."
                onDelete={handleUpdateIsRecommended}
              />
            )}
          </div>
        </div>

        <div className={styles.songs}>
          <div className={styles.topSection}>
            <div className={styles.topSectionTitle}>
              <p>Songs</p>
              <img src={audioIcon} alt="audio" />
            </div>
            <Button
              sx={{
                textTransform: "capitalize",
                height: "44px",
                width: "174px",
                backgroundColor: "#008000",
                borderRadius: "12px",
                "&:hover": {
                  backgroundColor: "#008000",
                },
              }}
              variant="contained"
              onClick={handleToggleMusicModal}
            >
              Add item
            </Button>
          </div>

          <div className={styles.songsContainer}>
            {!musicData &&
              Array(4)
                .fill(0)
                .map((_, index) => (
                  <div className={styles.song} key={index}>
                    <div className={styles.songInfoWrap}>
                      <Skeleton
                        variant="rectangular"
                        width={480}
                        height={156}
                        animation="wave"
                        sx={{ borderRadius: "8px" }}
                      />
                    </div>
                  </div>
                ))}
            {musicData &&
              musicData.length > 0 &&
              musicData
                .filter((audio) => audio.sections.includes("Recommendation"))
                .filter(
                  (audio, index, self) =>
                    index === self.findIndex((v) => v.uid === audio.uid)
                )
                .map((audio) => (
                  <AudioCard
                    key={audio.uid}
                    audio={audio}
                    isEdit={true}
                    handleToggleEditModal={handleToggleEditModalAudio}
                    handleToggleDeleteAudioModal={handleToggleDeleteAudioModal}
                  />
                ))}
            {musicData?.filter((audio) =>
              audio.sections.includes("Recommendation")
            ).length === 0 && <p style={{ marginLeft: "20px" }}>No data yet</p>}

            {isOpenAddVideo && (
              <AddFeaturedItem
                isOpen={isOpenAddVideo}
                setIsOpen={setIsOpenAddVideo}
                type="video"
              />
            )}
            {isOpenAddMusic && (
              <AddFeaturedItem
                isOpen={isOpenAddMusic}
                setIsOpen={setIsOpenAddMusic}
                type="music"
              />
            )}

            {isOpenModalDeleteMusic && (
              <DeleteModal
                isOpen={isOpenModalDeleteMusic}
                setIsOpen={setIsOpenModalDeleteMusic}
                name={selectedAudio ? selectedAudio.title : ""}
                supportingText="Remove song? The song will be removed from the Featured tab. Don't worry, it will not be deleted from the app."
                onDelete={handleUpdateIsRecommendedSong}
              />
            )}

            {isOpenEditModalAudio && (
              <EditMusicModal
                isOpenEditModal={isOpenEditModalAudio}
                setIsOpenEditModal={setIsOpenEditModalAudio}
                selectedMusic={selectedAudio}
                setSelectedMusic={setSelectedAudio}
                handleEditMusic={handleEditMusic}
              />
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export const HomeContent = withAdmin(HomeContentCmp);
HomeContentCmp.displayName = "HomeContentCmp";

